import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { HiFilter, HiSearch, HiX } from 'react-icons/hi';

const API =  process.env.REACT_APP_API_URL;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch products and categories with cache-busters
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/api/products?_=${Date.now()}`),
          axios.get(`${API}/api/categories?_=${Date.now()}`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data.filter(cat => cat.name)); // exclude null names
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/600';
    if (url.startsWith('/uploads/')) {
      return `${API}/images/${url.replace('/uploads/', '')}`;
    }
    return url;
  };

  // Filtering logic
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory
      ? product.categoryId === selectedCategory
      : true;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      {/* Header row with filter toggle */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900"></h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black transition"
        >
          <HiFilter size={18} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Collapsible filter bar */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search by name */}
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:border-black outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <HiX size={16} />
              </button>
            )}
          </div>

          {/* Category dropdown */}
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none bg-white"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <p className="text-center text-gray-500 py-20">Loading...</p>
      ) : filteredProducts.length === 0 ? (
        <p className="text-center text-gray-500 py-20">
          {searchTerm || selectedCategory
            ? 'No products match your filters.'
            : 'No products found.'}
        </p>
      ) : (
        /* Product grid – identical to your original design */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProducts.map(product => {
            const isFutureDrop =
              product.releaseDate && new Date(product.releaseDate) > new Date();

            // Future drop – static, blurred, no link
            if (isFutureDrop) {
              return (
                <div key={product.id} className="product-card coming-soon">
                  <div className="relative aspect-[3/4] bg-transparent">
                    <img
                      src={getImageUrl(product.imageUrls?.[0])}
                      alt={product.name}
                      className="w-full h-full object-cover blur-md filter"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-white text-xs font-semibold uppercase tracking-widest bg-black/50 px-3 py-1 rounded">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </div>
              );
            }

            // Live product – animated, clickable
            return (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="product-card"
              >
                <div className="aspect-[3/4] bg-transparent">
                  <img
                    src={getImageUrl(product.imageUrls?.[0])}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}